import { tagScriptRuns } from "@/lib/scriptFonts";

export interface Cue { start: number; end: number; text: string }

export interface SubtitleStyle {
  /** fraction of the shorter video side; small .035, medium .05, large .07 */
  sizeFraction: number;
  position: "bottom" | "middle" | "top";
  color: "white" | "yellow";
  background: boolean;
}

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = { sizeFraction: 0.05, position: "bottom", color: "white", background: false };

const TIME = /(\d{1,2}):(\d{2}):(\d{2})[,.](\d{1,3})/;

function toSeconds(m: RegExpMatchArray): number {
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4].padEnd(3, "0")) / 1000;
}

/** Converts WebVTT or ASS/SSA subtitle text to SRT so one parser handles all three; SRT passes through. */
export function toSrt(raw: string): string {
  const text = raw.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  if (/^\s*\[Script Info\]/im.test(text) || /^Dialogue:/m.test(text)) {
    const fmt = /^Format:\s*(Layer|Marked),.*Start,\s*End.*Text\s*$/m.exec(text)?.[0];
    const cols = fmt ? fmt.replace(/^Format:\s*/, "").split(",").map((c) => c.trim()) : ["Layer", "Start", "End", "Style", "Name", "MarginL", "MarginR", "MarginV", "Effect", "Text"];
    const iS = cols.indexOf("Start"), iE = cols.indexOf("End"), iT = cols.indexOf("Text");
    const at = (t: string) => { const m = /(\d+):(\d{2}):(\d{2})\.(\d{1,2})/.exec(t.trim()); return m ? `${m[1].padStart(2, "0")}:${m[2]}:${m[3]},${m[4].padEnd(3, "0")}` : t.trim(); };
    const out: string[] = [];
    for (const line of text.split("\n")) {
      if (!/^Dialogue:/i.test(line)) continue;
      const parts = line.replace(/^Dialogue:\s*/i, "").split(",");
      const body = parts.slice(iT).join(",").replace(/\{[^}]*\}/g, "").replace(/\\N/gi, "\n").trim();
      if (body) out.push(`${out.length + 1}\n${at(parts[iS])} --> ${at(parts[iE])}\n${body}`);
    }
    return out.join("\n\n");
  }
  if (/^\s*WEBVTT/.test(text)) {
    const blocks = text.replace(/^\s*WEBVTT[^\n]*\n/, "").trim().split(/\n{2,}/).filter((b) => b.includes("-->"));
    return blocks.map((b, i) => {
      const lines = b.split("\n");
      const ti = lines.findIndex((l) => l.includes("-->"));
      const timing = lines[ti].replace(/(\s+(align|position|line|size):\S+)+\s*$/g, "").replace(/(^|\s)(\d{2}:\d{2})\.(\d{3})/g, "$100:$2,$3").replace(/(\d{2}:\d{2}:\d{2})\.(\d{3})/g, "$1,$2");
      return `${i + 1}\n${timing}\n${lines.slice(ti + 1).join("\n")}`;
    }).join("\n\n");
  }
  return text;
}

/** Parse SRT text. Throws an Error whose message says which line is wrong, in plain words. */
export function parseSrt(raw: string): Cue[] {
  const text = raw.replace(/^﻿/, "").replace(/\r\n?/g, "\n").trim();
  if (!text) throw new Error("The subtitles are empty.");
  const blocks = text.split(/\n{2,}/);
  const cues: Cue[] = [];
  let lineNo = 1;
  for (const block of blocks) {
    const lines = block.split("\n");
    let i = 0;
    if (/^\d+$/.test(lines[0].trim())) i = 1; // the cue number is optional
    const timing = lines[i] ?? "";
    const parts = timing.split("-->");
    const a = parts[0]?.match(TIME);
    const b = parts[1]?.match(TIME);
    if (parts.length !== 2 || !a || !b) {
      throw new Error(`Line ${lineNo + i}: expected a time like "00:00:01,000 --> 00:00:04,000" but found "${timing.trim().slice(0, 40)}".`);
    }
    const start = toSeconds(a), end = toSeconds(b);
    if (end <= start) throw new Error(`Line ${lineNo + i}: the end time must be after the start time.`);
    const body = lines.slice(i + 1).join("\n").trim();
    if (body) cues.push({ start, end, text: body });
    lineNo += lines.length + 1;
  }
  if (!cues.length) throw new Error("No subtitle text was found. Each entry needs a time line followed by the text.");
  return cues;
}

const assTime = (t: number) => {
  const cs = Math.round(t * 100);
  const h = Math.floor(cs / 360000), m = Math.floor((cs % 360000) / 6000), s = Math.floor((cs % 6000) / 100), c = cs % 100;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(c).padStart(2, "0")}`;
};

/** ASS text for libass. Script fonts are chosen per run (see tagScriptRuns); width/height = the video's size. */
export function buildAss(cues: Cue[], width: number, height: number, style: SubtitleStyle = DEFAULT_SUBTITLE_STYLE): string {
  const size = Math.max(14, Math.round(Math.min(width, height) * style.sizeFraction));
  const align = style.position === "top" ? 8 : style.position === "middle" ? 5 : 2;
  const colour = style.color === "yellow" ? "&H0000FFFF" : "&H00FFFFFF";
  const marginV = Math.round(height * 0.05);
  const border = style.background ? 3 : 1;
  const back = style.background ? "&H99000000" : "&H00000000";
  const lines = [
    "[Script Info]",
    "ScriptType: v4.00+",
    `PlayResX: ${width}`,
    `PlayResY: ${height}`,
    "WrapStyle: 0",
    "ScaledBorderAndShadow: yes",
    "",
    "[V4+ Styles]",
    "Format: Name,Fontname,Fontsize,PrimaryColour,SecondaryColour,OutlineColour,BackColour,Bold,Italic,Underline,StrikeOut,ScaleX,ScaleY,Spacing,Angle,BorderStyle,Outline,Shadow,Alignment,MarginL,MarginR,MarginV,Encoding",
    `Style: Default,Roboto,${size},${colour},&H000000FF,&H00000000,${back},0,0,0,0,100,100,0,0,${style.background ? 4 : 1},${border},0,${align},${Math.round(width * 0.05)},${Math.round(width * 0.05)},${marginV},1`,
    "",
    "[Events]",
    "Format: Layer,Start,End,Style,Name,MarginL,MarginR,MarginV,Effect,Text",
  ];
  for (const c of cues) {
    const clean = c.text.replace(/<[^>]*>/g, "").replace(/[{}\\]/g, "").split("\n").map((l) => tagScriptRuns(l.trim())).join("\\N");
    lines.push(`Dialogue: 0,${assTime(c.start)},${assTime(c.end)},Default,,0,0,0,,${clean}`);
  }
  return lines.join("\n") + "\n";
}
